'use client'

import { Card, Cell, Checkbox, Container, Spacer, Text } from '@vinted/web-ui'
import { useContext, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { formatCurrency } from '@marketplace-web/currency/currency-data'
import { useDebounce } from '@marketplace-web/shared/ui-helpers'
import { HelpCenterFaqEntryUrl } from '@marketplace-web/help-center/help-center-faq-entry-url-feature'
import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'

import {
  AccessChannel,
  FaqEntryType,
} from '@marketplace-web/help-center/help-center-faq-entry-url-data'
import {
  itemUploadClickEvent,
  userInputEvent,
  getBumpMinimumPrice,
  useGetBumpMinimumPriceApiGateway,
  transformBumpMinimumPriceResponse,
} from '@marketplace-web/vas/vas-data'
import {
  ItemUploadFieldName,
  ItemStatus,
  UiStateContext,
  useAttributesContext,
  useFieldVisibility,
} from '@marketplace-web/item-upload/item-upload-data'

import BumpValuePropositionModalV3 from '../../Bumps/BumpCheckout/BumpValuePropositionV3'

type Props = { tempUuid: string }

const BumpItemCheckbox = ({ tempUuid }: Props) => {
  const getBumpMinimumPriceApiGateway = useGetBumpMinimumPriceApiGateway()
  const { track } = useTracking()
  const { locale } = useIntl()
  const {
    screen,
    itemStatus: [itemStatus],
    canBumpItem: [canBumpItem],
  } = useContext(UiStateContext)
  const { attributes, isBumpChecked, setIsBumpChecked } = useAttributesContext()
  const isOnHoliday = useSession().user?.is_on_holiday
  const trackExpose = useTrackAbTestCallback()

  const bumpValuePropositionInListingFormAbTest = useAbTest(
    'bump_value_proposition_in_listing_form_v2',
  )

  const canShowBumpCheckbox =
    attributes.price !== null && (itemStatus !== ItemStatus.Edit || canBumpItem) && !isOnHoliday

  const [showValuePropositionModal, setShowValuePropositionModal] = useState(false)

  const isApiGatewayClientEnabled = useFeatureSwitch('web_api_gateway_client')
  const isApiGatewayClientEnabledForVas = useFeatureSwitch('web_api_gateway_client_vas')
  const isApiGatewayEnabled = isApiGatewayClientEnabled && isApiGatewayClientEnabledForVas

  const getBumpMinimumPriceRequest = isApiGatewayEnabled
    ? getBumpMinimumPriceApiGateway
    : getBumpMinimumPrice

  const {
    fetch: fetchBumpMinimumPrice,
    transformedData: minimumPriceOption,
    isLoading,
  } = useFetch(getBumpMinimumPriceRequest, transformBumpMinimumPriceResponse)

  const translate = useTranslate('item_upload')
  const translateA11y = useTranslate('bump_items.a11y')

  useFieldVisibility({
    fieldName: ItemUploadFieldName.Bump,
    isVisible: canShowBumpCheckbox,
  })

  const debouncedFetchBumpMinimumPrice = useDebounce(fetchBumpMinimumPrice, 1000, false)

  useEffect(() => {
    if (!canShowBumpCheckbox || !attributes.price) return

    debouncedFetchBumpMinimumPrice(attributes.price)
  }, [debouncedFetchBumpMinimumPrice, canShowBumpCheckbox, attributes.price])

  function handleCheckboxClick() {
    track(
      userInputEvent({
        screen,
        value: Number(!isBumpChecked).toString(),
        state: 'focus',
        tempUuid,
        target: ItemUploadFieldName.Bump,
      }),
    )
    setIsBumpChecked(!isBumpChecked)
  }

  function handleLearnMoreClick() {
    if (bumpValuePropositionInListingFormAbTest?.variant === 'on') {
      setShowValuePropositionModal(true)
    }

    track(
      itemUploadClickEvent({
        screen,
        target: 'push_up_learn_more',
        uploadSessionId: tempUuid,
      }),
    )
    trackExpose(bumpValuePropositionInListingFormAbTest)
  }

  const renderCheckbox = () => {
    if (isLoading) {
      return (
        <ContentLoader styling={ContentLoader.Styling.Narrow} size={ContentLoader.Size.Large} />
      )
    }

    if (!minimumPriceOption) return null

    return (
      <Container>
        <Checkbox
          name={ItemUploadFieldName.Bump}
          checked={isBumpChecked}
          onClick={handleCheckboxClick}
          testId="bump-checkbox"
          aria={{ 'aria-labelledby': 'bump-title' }}
          onChange={() => undefined}
        />
      </Container>
    )
  }

  const renderLearnMoreLink = () => {
    if (bumpValuePropositionInListingFormAbTest?.variant === 'on') {
      return (
        <button onClick={handleLearnMoreClick} type="button" key="learn-more-button">
          <Text as="span" text={translate('bump.learn_more')} type="subtitle" underline clickable />
        </button>
      )
    }

    return (
      <HelpCenterFaqEntryUrl
        type={FaqEntryType.Bump}
        accessChannel={AccessChannel.ProductLink}
        render={url => (
          <a href={url} target="_blank" rel="noreferrer" onClick={handleLearnMoreClick}>
            <Text
              as="span"
              text={translate('bump.learn_more')}
              type="subtitle"
              width="parent"
              clickable
            />
          </a>
        )}
      />
    )
  }

  const renderTitle = () => {
    const title = minimumPriceOption?.hasFreeBumps
      ? translate('free_bump.header')
      : translate('bump.header_v2')

    return <Text as="span" text={title} type="title" width="parent" id="bump-title" />
  }

  const renderPrice = () => {
    if (!minimumPriceOption) return null

    const price = formatCurrency(
      minimumPriceOption.hasFreeBumps ? 0 : minimumPriceOption.finalPrice.amount,
      {
        locale,
        currency: minimumPriceOption.finalPrice.currencyCode,
      },
    )

    const hasDiscount = minimumPriceOption.discountAmount.amount > 0
    const isOriginalPriceVisible = minimumPriceOption.hasFreeBumps || hasDiscount

    const priceText = minimumPriceOption.hasFreeBumps ? price : translate('bump.price', { price })

    const basePrice = formatCurrency(minimumPriceOption.basePrice.amount, {
      locale,
      currency: minimumPriceOption.basePrice.currencyCode,
    })
    const originalPriceText = isOriginalPriceVisible
      ? translateA11y('price', {
          price: undefined,
          original_price: basePrice,
        })
      : ''

    return (
      <div aria-label={priceText + originalPriceText}>
        <Spacer size="small" />
        <Text
          text={priceText}
          as="p"
          type="subtitle"
          theme="amplified"
          aria={{ 'aria-hidden': 'true' }}
        />
        {isOriginalPriceVisible && (
          <>
            <Spacer size="small" orientation="vertical" />
            <Text
              text={basePrice}
              as="p"
              type="subtitle"
              strikethrough
              theme="muted"
              aria={{ 'aria-hidden': 'true' }}
            />
          </>
        )}
        <Spacer size="small" />
      </div>
    )
  }

  if (!canShowBumpCheckbox) return null

  return (
    <>
      <Spacer size="x-large" />
      <Card>
        <Container styling="wide">
          <Cell styling="tight" title={renderTitle()} suffix={renderCheckbox()}>
            {renderPrice()}
            <Text type="subtitle" text={translate('bump.body')} as="p" width="parent" />
            {renderLearnMoreLink()}
          </Cell>
        </Container>
      </Card>
      <BumpValuePropositionModalV3
        show={showValuePropositionModal}
        showCloseButton={false}
        onCloseOrConfirm={() => setShowValuePropositionModal(false)}
      />
    </>
  )
}

export default BumpItemCheckbox
